import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Send } from "lucide-react";
import { useI18n } from "@/i18n";

/** «Scrivi all'azienda» del Portale B2B: messaggio libero salvato in piattaforma + notifica al Commerciale. */
export function ContactCompanyDialog({ open, onOpenChange }) {
  const { t } = useI18n();
  const [subject, setSubject] = useState("");
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!msg.trim()) { toast.error(t("toast.write_message")); return; }
    setSending(true);
    try {
      await api.post("/contact-sales", { subject: subject.trim(), message: msg.trim() });
      toast.success(t("toast.contact_sent"));
      onOpenChange(false); setMsg(""); setSubject("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("toast.contact_failed"));
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-border bg-brema-card" data-testid="contact-sales-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground">{t("pf.contact_title")}</DialogTitle>
          <DialogDescription>{t("pf.contact_desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("pf.subject")}</Label>
            <Input data-testid="contact-sales-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
              className="bg-brema-bg text-sm" placeholder={t("pf.subject_ph")} maxLength={200} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("pf.message")} <span className="text-brand">*</span></Label>
            <Textarea data-testid="contact-sales-message" value={msg} onChange={(e) => setMsg(e.target.value)}
              className="min-h-[130px] bg-brema-bg text-sm" placeholder={t("pf.contact_ph")} maxLength={5000} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="contact-sales-cancel"
            className="rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">{t("common.cancel")}</Button>
          <Button data-testid="contact-sales-send" onClick={send} disabled={sending || !msg.trim()}
            className="rounded-full bg-brema-red font-semibold text-white hover:bg-brema-red/90 disabled:opacity-40">
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} {t("pf.send_request")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
